import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'

import { dmsState } from '@/app/store'
import { type IUpdateProgramDto, type IAddProgramDto, type ICompanyProgramItem } from '@/app/types/dmsTypes'
import { onCheckDisabledConditions } from '@/app/utils'
import { RegularField } from '@/pages/Dms/NestedPages/RegularField/RegularField'
import { TextAreaField } from '@/pages/Dms/NestedPages/TextAreaField/TextAreaField'

import style from './Item.module.scss'
import { useFields } from './useFields'

type TItemMode = 'add' | 'change'

interface IProps extends ICompanyProgramItem {
    mode: TItemMode
    contractId: string
    onZeroNewProgram?: () => void
}

export const Item = observer((props: IProps) => {
    const { mode, contractId, onZeroNewProgram, ...rest } = props

    const [isEdit, setIsEdit] = useState<boolean>(false)

    const {
        name,
        onChangeName,
        onSetIsTouchedName,
        description,
        onChangeDescription,
        onSetIsTouchedDescription,
        isSomeToched,
        onSetTouchAll
    } = useFields({ data: rest })

    const additionDto: IAddProgramDto = {
        name: name.value || '',
        contractId,
        description: description.value || ''
    }

    const upgateDto: IUpdateProgramDto = {
        id: rest.id,
        name: name.value || '',
        description: description.value || ''
    }

    const onAddProgramHandler = () => {
        if (!isValidFields || isSomeToched) {
            dmsState.onAddProgram(additionDto, onZeroNewProgram)
        } else {
            onSetTouchAll()
        }
    }

    const onSaveProgramChanges = () => {
        if (isEdit) {
            dmsState.onUpdateProgram(upgateDto, () => setIsEdit(false))
        } else {
            setIsEdit(true)
        }
    }

    const onRemoveProgramHandler = () => {
        if (isEdit) {
            setIsEdit(false)
        } else {
            dmsState.onRemoveProgram(rest.id, contractId)
        }
    }

    const isValidFields = onCheckDisabledConditions(Boolean(!name.error), Boolean(!description.error))

    const onSetDisabledMode = (): boolean => {
        if (!isSomeToched) {
            return false
        }
        if (!isValidFields) {
            return false
        }
        return true
    }

    const actions = [
        {
            title: isEdit ? 'Сохранить изменения' : 'Редактировать',
            type: 'primary',
            disabled: isEdit ? onSetDisabledMode() : false,
            callback: onSaveProgramChanges,
            belongTo: 'change'
        },
        {
            title: isEdit ? 'Отмена' : 'Удалить',
            type: 'remove',
            disabled: false,
            callback: onRemoveProgramHandler,
            belongTo: 'change'
        },
        {
            title: 'Добавить',
            type: 'primary',
            disabled: onSetDisabledMode(),
            callback: onAddProgramHandler,
            belongTo: 'add'
        },
        {
            title: 'Отмена',
            type: 'remove',
            disabled: false,
            callback: onZeroNewProgram,
            belongTo: 'add'
        }
    ]

    return (
        <div className={style.container}>
            <div className={style.name}>
                <RegularField
                    entity={name}
                    onChange={onChangeName}
                    onSetIsTouched={onSetIsTouchedName}
                    disabled={!isEdit && mode === 'change'}
                />
            </div>
            <div className={style.description}>
                <TextAreaField
                    withMarkdown
                    entity={description}
                    onChange={onChangeDescription}
                    onSetIsTouched={onSetIsTouchedDescription}
                    disabled={!isEdit && mode === 'change'}
                />
            </div>
            <div className={style.actions}>
                {actions
                    .filter((el) => el.belongTo === mode)
                    .map((action) => {
                        const { title, callback, type, disabled } = action
                        return (
                            <button
                                key={title}
                                className={classNames(style.action, style[type])}
                                onClick={callback}
                                disabled={disabled}
                            >
                                {title}
                            </button>
                        )
                    })}
            </div>
        </div>
    )
})
